import { formatTime, formatDuration, CHICAGO_TZ } from "$lib/utilities/eventDate.js";
import { formatEventTime } from "$lib/utilities/eventFormatters.js";
import { convertToEventDetailUrl } from "$lib/utilities/url-helpers.js";

/**
 * Formats an ISO date string to YYYY-MM-DD in Chicago timezone
 */
const formatDateInChicago = (isoString) => {
  const d = new Date(isoString);
  const year = d.toLocaleString("en-US", { timeZone: CHICAGO_TZ, year: "numeric" });
  const month = d.toLocaleString("en-US", { timeZone: CHICAGO_TZ, month: "2-digit" });
  const day = d.toLocaleString("en-US", { timeZone: CHICAGO_TZ, day: "2-digit" });
  return `${year}-${month}-${day}`;
};

export const mapEventFeatures = (addOns = []) => {
  const featureMap = {
    "ASL Available": "sign-language",
    "Audio Device Available": "audio-guide"
  };

  return addOns.reduce((features, addOn) => {
    if (featureMap[addOn]) {
      features.push(featureMap[addOn]);
    }
    return features;
  }, []);
};

export const mapEventToScheduleCard = (event) => ({
  eventTitle: event.title,
  eventTime: formatTime(event.start),
  eventDuration: formatDuration(event.start, event.end),
  eventDescription: event.description,
  eventAlert: event.alertText || "",
  cta: event.registrationUrl
    ? {
        text: "Get tickets",
        url: event.registrationUrl
      }
    : {
        text: "Learn more",
        url: convertToEventDetailUrl(event?.slug)
      },
  eventLocation: {
    name: event.specificLocation?.fields.locationName || event.venue?.fields?.name || ""
  },
  eventPricing: event.price || "",
  eventFeatures: mapEventFeatures(event.addOns),
  theme: event.theme || "white"
});

export const groupEventsByDay = (events, featuredItems = []) => {
  if (!events?.length) {
    return [];
  }

  const eventsByDay = events.reduce((groups, event) => {
    const date = formatDateInChicago(event.start);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(event);
    return groups;
  }, {});

  const featuredByDay = featuredItems.reduce((groups, item) => {
    const date = formatDateInChicago(item.start);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(item);
    return groups;
  }, {});

  // Convert to array and sort chronologically
  return Object.entries(eventsByDay)
    .map(([date, dayEvents]) => {
      const featuredItem = featuredByDay[date]?.[0];

      const items = featuredItem
        ? [
            mapEventToScheduleCard(featuredItem),
            ...dayEvents
              .filter((event) => event.slug !== featuredItem.slug)
              .sort((a, b) => new Date(a.start) - new Date(b.start))
              .map(mapEventToScheduleCard)
          ]
        : dayEvents
            .sort((a, b) => new Date(a.start) - new Date(b.start))
            .map(mapEventToScheduleCard);

      return {
        date,
        title: formatDateForTourDay(date),
        hasFeaturedItem: !!featuredItem,
        items
      };
    })
    .sort((a, b) => a.date.localeCompare(b.date));
};

export const formatDateForTourDay = (dateString) => {
  // dateString is in YYYY-MM-DD format
  // Create a date at noon to avoid timezone issues
  const date = new Date(`${dateString}T12:00:00`);
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: CHICAGO_TZ
  });
};

export const getRelatedLists = (relatedTours, relatedEvents) => {
  let relatedToursData = relatedTours.map((tour) => {
    return {
      id: tour.id,
      eventTitle: tour.title,
      eventDescription: tour.description,
      eventTime: formatTime(tour.startDateTime),
      eventDuration: formatDuration(tour.startDateTime, tour.endDateTime),
      eventLocation: { name: tour.specificLocation?.locationName ?? tour.venue.name },
      eventPricing: tour.priceLabel,
      eventFeatures: mapEventFeatures(tour.addOns),
      cta: tour.registrationUrl
        ? {
            text: "Get tickets",
            url: tour.registrationUrl
          }
        : {
            text: "Learn more",
            url: convertToEventDetailUrl(tour.slug)
          },
      theme: tour.theme,
      image: tour.image,
      imageUrl: tour.imageUrl
    };
  });

  let relatedEventsData = relatedEvents.map((event) => {
    const eventTime = formatEventTime(event.startDateTime, event.endDateTime);
    return {
      id: event.id,
      eventTitle: event.title,
      eventDescription: event.summary,
      eventDate: eventTime.date,
      eventTime: eventTime.time,
      eventLocation: event.specificLocation?.locationName ?? event.venue.name,
      eventPricing: event.priceLabel,
      cta: event.registrationUrl
        ? {
            text: "Get tickets",
            url: event.registrationUrl
          }
        : {
            text: "Learn more",
            url: convertToEventDetailUrl(event.slug)
          },
      theme: event.theme,
      image: event.image,
      imageUrl: event.imageUrl
    };
  });
  return {
    relatedToursData,
    relatedEventsData
  };
};
